import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

interface SuiviMessageResponse {
  message: string;
  returnCode: number;
  trackingNumber: string;
}

interface CapteurLivraisonFields {
  id: number;
  reference_envoi: string | null;
  code_bon_retour: string | null;
  code_bon_retour_cartouches: string | null;
  cartouche_a: number | null;
  cartouche_b: number | null;
  statut_livraison_envoi: number | null;
  statut_livraison_retour_capteur: number | null;
  statut_livraison_retour_cartouches: number | null;
  message_livre_envoi: string | null;
  message_livre_retour_capteur: string | null;
  message_livre_retour_cartouches: string | null;
}

interface StatutsLivraisonMap {
  en_stock: number;
  en_cours_de_transit: number;
  livre: number;
}

interface CapteursLivraisonsListResponse {
  items: CapteurLivraisonFields[];
  count: number;
  statuts: StatutsLivraisonMap;
}

interface CapteurLivraisonPatchPayload {
  statut_livraison_envoi?: number;
  message_livre_envoi?: string;
  statut_livraison_retour_capteur?: number;
  message_livre_retour_capteur?: string;
  statut_livraison_retour_cartouches?: number;
  message_livre_retour_cartouches?: string;
}

export interface CheckLivraisonsResult {
  scanned: number;
  updatedCapteurs: number;
  markedCartouchesLivrees: number;
  trackingCalls: number;
  errors: Array<{ capteurId: number; step: string; error: string }>;
}

type StatutLivraisonNom = 'en stock' | 'en cours de transit' | 'livre';

@Injectable({ providedIn: 'root' })
export class CheckLivraisonsService {
  private readonly apiBaseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  async runCheckLivraisons(queCartouches = false): Promise<CheckLivraisonsResult> {
    const result: CheckLivraisonsResult = {
      scanned: 0,
      updatedCapteurs: 0,
      markedCartouchesLivrees: 0,
      trackingCalls: 0,
      errors: [],
    };

    const response = await firstValueFrom(
      this.http.get<CapteursLivraisonsListResponse>(`${this.apiBaseUrl}/api/capteurs/livraisons/fields`)
    );

    const capteurs = response?.items ?? [];
    const statuts = response?.statuts;
    if (!statuts) {
      throw new Error('Statuts de livraison indisponibles.');
    }

    result.scanned = capteurs.length;

    for (const capteur of capteurs) {
      const patch: CapteurLivraisonPatchPayload = {};
      let hasPatch = false;

      try {
        if (!queCartouches) {
          const envoiStatus = capteur.statut_livraison_envoi === statuts.livre
            ? { statut: 'livre' as StatutLivraisonNom, message: capteur.message_livre_envoi }
            : await this.fetchTrackingStatus(capteur.reference_envoi, result);
          const envoiStatusId = this.toStatutId(envoiStatus.statut, statuts);
          if (capteur.statut_livraison_envoi !== envoiStatusId) {
            patch.statut_livraison_envoi = envoiStatusId;
            hasPatch = true;
          }
          if (envoiStatus.message && envoiStatus.message !== capteur.message_livre_envoi) {
            patch.message_livre_envoi = envoiStatus.message;
            hasPatch = true;
          }

          const retourCapteurStatus = capteur.statut_livraison_retour_capteur === statuts.livre
            ? { statut: 'livre' as StatutLivraisonNom, message: capteur.message_livre_retour_capteur }
            : await this.fetchTrackingStatus(capteur.code_bon_retour, result);
          const retourCapteurStatusId = this.toStatutId(retourCapteurStatus.statut, statuts);
          if (capteur.statut_livraison_retour_capteur !== retourCapteurStatusId) {
            patch.statut_livraison_retour_capteur = retourCapteurStatusId;
            hasPatch = true;
          }
          if (retourCapteurStatus.message && retourCapteurStatus.message !== capteur.message_livre_retour_capteur) {
            patch.message_livre_retour_capteur = retourCapteurStatus.message;
            hasPatch = true;
          }
        }

        const retourCartouchesStatus = capteur.statut_livraison_retour_cartouches === statuts.livre
          ? { statut: 'livre' as StatutLivraisonNom, message: capteur.message_livre_retour_cartouches }
          : await this.fetchTrackingStatus(capteur.code_bon_retour_cartouches, result);
        const retourCartouchesStatusId = this.toStatutId(retourCartouchesStatus.statut, statuts);
        if (capteur.statut_livraison_retour_cartouches !== retourCartouchesStatusId) {
          patch.statut_livraison_retour_cartouches = retourCartouchesStatusId;
          hasPatch = true;
        }
        if (retourCartouchesStatus.message && retourCartouchesStatus.message !== capteur.message_livre_retour_cartouches) {
          patch.message_livre_retour_cartouches = retourCartouchesStatus.message;
          hasPatch = true;
        }

        if (hasPatch) {
          await firstValueFrom(
            this.http.patch(`${this.apiBaseUrl}/api/capteurs/${capteur.id}/livraisons`, patch)
          );
          result.updatedCapteurs += 1;
        }

        if (retourCartouchesStatus.statut === 'livre') {
          const cartoucheIds = [capteur.cartouche_a, capteur.cartouche_b]
            .filter((id): id is number => id !== null)
            .filter((id, idx, arr) => arr.indexOf(id) === idx);

          if (cartoucheIds.length > 0) {
            await firstValueFrom(
              this.http.patch(`${this.apiBaseUrl}/api/cartouches/livraisons/mark-livrees`, {
                cartouche_ids: cartoucheIds,
              })
            );
            result.markedCartouchesLivrees += cartoucheIds.length;
          }
        }
      } catch (err: any) {
        result.errors.push({
          capteurId: capteur.id,
          step: 'processing',
          error: this.extractErrorMessage(err),
        });
      }
    }

    return result;
  }

  private toStatutId(statut: StatutLivraisonNom, statuts: StatutsLivraisonMap): number {
    if (statut === 'livre') {
      return statuts.livre;
    }
    if (statut === 'en stock') {
      return statuts.en_stock;
    }
    return statuts.en_cours_de_transit;
  }

  private async fetchTrackingStatus(
    trackingNumber: string | null,
    result: CheckLivraisonsResult
  ): Promise<{ statut: StatutLivraisonNom; message: string | null }> {
    const cleaned = (trackingNumber ?? '').trim();
    if (!cleaned) {
      return { statut: 'en stock', message: null };
    }

    result.trackingCalls += 1;

    try {
      const response = await firstValueFrom(
        this.http.get<SuiviMessageResponse>(`${this.apiBaseUrl}/api/suivi-colis/${encodeURIComponent(cleaned)}`)
      );

      const message = (response?.message ?? '').trim();
      if (!message) {
        return { statut: 'en cours de transit', message: null };
      }

      if (this.isPrisEnChargeMessage(message)) {
        return { statut: 'en stock', message };
      }

      if (this.isDeliveredMessage(message)) {
        return { statut: 'livre', message };
      }

      return { statut: 'en cours de transit', message };
    } catch {
      return { statut: 'en cours de transit', message: null };
    }
  }

  private isPrisEnChargeMessage(message: string): boolean {
    const normalized = message
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    return normalized.includes('pris en charge');
  }

  private isDeliveredMessage(message: string): boolean {
    const normalized = message
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    return (
      normalized.includes('livre') ||
      normalized.includes('distribution effectuee') ||
      normalized.includes('distribue') ||
      normalized.includes('remis')
    );
  }

  private extractErrorMessage(err: any): string {
    if (typeof err?.error === 'string' && err.error.trim()) {
      return err.error;
    }
    if (err?.error?.detail) {
      return String(err.error.detail);
    }
    if (err?.error?.message) {
      return String(err.error.message);
    }
    if (err?.message) {
      return String(err.message);
    }
    return 'Erreur inconnue';
  }
}
